import React, { FC, useMemo } from 'react';
import type { LandingLayoutType } from 'ts/client/types';
import type { ICoverPhoto } from 'ts/common/types';
import AccentLayout from './AccentLayout';
import BannerLayout from './BannerLayout';
import Block2LeftLayout from './Block2LeftLayout';
import Block2RightLayout from './Block2RightLayout';
import BlockLeftLayout from './BlockLeftLayout';
import BlockRightLayout from './BlockRightLayout';
import BorderLineLayout from './BorderLineLayout';
import BorderThickLayout from './BorderThickLayout';
import BoxLeftLayout from './BoxLeftLayout';
import BoxRightLayout from './BoxRightLayout';
import DetailLayout from './DetailLayout';
import DetailRightLayout from './DetailRightLayout';
import FoundationLayout from './FoundationLayout';
import HeadlineLayout from './HeadlineLayout';
import LimelightLayout from './LimelightLayout';
import LinesLayout from './LinesLayout';
import MarqueeLayout from './MarqueeLayout';
import SimpleLeftLayout from './SimpleLeftLayout';
import SimpleRightLayout from './SimpleRightLayout';
import SquareLayout from './SquareLayout';
import SymmetryLayout from './SymmetryLayout';

export const coverAnimation = '2s ease-in-out fadeIn';
export const contentAnimation = '1s ease-in-out fadeIn 2s forwards';

export interface ILayout {
    galleryTitle: string;
    brandName: string;
    eventBrandLogo?: Nullable<SpApi.IBrandLogoImage>;
    logoAltText: string;
    colorScheme: 'dark' | 'light';
    coverPhotoUrl: Nullable<string>;
    buttonText: string;
    buttonTextLarge?: string;
    onClick: () => void;
    enableAnimations?: boolean;
}

export type IBaseLayoutProps = ILayout & {
    landingLayoutType: LandingLayoutType;
    coverPhoto: ICoverPhoto | false;
};

export type ILayoutProps = ILayout & {
    coverPhoto: ICoverPhoto;
};

const BaseLayout: FC<IBaseLayoutProps> = ({ landingLayoutType, coverPhoto, ...layoutProps }) => {
    // The gallery music control is placed around each layout's content (see
    // introMusicControlPlacement.ts), so a change to this mapping, or to where a layout puts its
    // content, needs a look there too.
    const SelectedLayout = useMemo(() => {
        switch (landingLayoutType) {
            case 'full-bottom-alt':
                return FoundationLayout;
            case 'split':
                return DetailLayout;
            case 'full-center':
                return MarqueeLayout;
            case 'full-bottom':
                return LimelightLayout;
            case 'basic':
                return AccentLayout;
            case 'hexagon':
                return SymmetryLayout;
            case 'border-thick':
                return BorderThickLayout;
            case 'border-line':
                return BorderLineLayout;
            case 'square':
                return SquareLayout;
            case 'lines':
                return LinesLayout;
            case 'detail-right':
                return DetailRightLayout;
            case 'block-left':
                return BlockLeftLayout;
            case 'block-right':
                return BlockRightLayout;
            case 'block2-left':
                return Block2LeftLayout;
            case 'block2-right':
                return Block2RightLayout;
            case 'box-left':
                return BoxLeftLayout;
            case 'box-right':
                return BoxRightLayout;
            case 'simple-right':
                return SimpleRightLayout;
            case 'simple-left':
                return SimpleLeftLayout;
            case 'banner':
                return BannerLayout;
            default:
                return HeadlineLayout;
        }
    }, [landingLayoutType]);

    if (!coverPhoto) {
        return <HeadlineLayout {...layoutProps} />;
    }

    return <SelectedLayout {...layoutProps} coverPhoto={coverPhoto} />;
};

export default BaseLayout;
